import { LOCALE, TIMEZONE } from '@lib/constants';
import { Category } from '@lib/features/categories';
import { makeNonNullableArray, toNumber } from '@liquorice/allsorts-craftcms-nextjs';
import dayjs from 'dayjs';

export const fmtCategoryNames = (
  maybeCats?: MaybeArrayOf<Category>,
  join = ', ',
  exclude?: MaybeArrayOf<Category>
) => {
  const excludeIds = makeNonNullableArray(exclude).map((v) => v.id);
  const cats = makeNonNullableArray(maybeCats).filter((v) => !excludeIds.includes(v.id));

  return cats.map((v) => v.title).join(join);
};

export const isDateRange = (start?: string | number | null, end?: string | number | null) => {
  return !!start && !!end && dayjs(start).format('YYYY-MM-DD') !== dayjs(end).format('YYYY-MM-DD');
};

export const fmtDate = (n?: string | number | null) => {
  if (!n) return undefined;
  const date = new Date(n);
  return `${date.getDate()} ${date.toLocaleString(LOCALE, {
    month: 'short',
    timeZone: TIMEZONE,
  })}, ${date.getFullYear()}`;
};

export const fmtDateRange = (
  start?: string | number | null,
  end?: string | number | null,
  join = '-'
) => {
  if (!start) return fmtDate(end);
  if (!end) return fmtDate(start);
  // console.log('start', start);
  // console.log('end', end);

  const formattedDate = new Intl.DateTimeFormat(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: TIMEZONE,
  });

  const makeFormattedDate = (date: string | number) => {
    return formattedDate.format(new Date(date));
  };

  const startStr = makeFormattedDate(start);
  const endStr = makeFormattedDate(end);

  if (startStr === endStr) return endStr;

  const [startDay, startMonth, startYear] = startStr.split(' ');
  const [endDay, endMonth, endYear] = endStr.split(' ');

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startDay}${join}${endStr}`;
    }
    return `${startDay} ${startMonth} ${join}${endStr}`;
  }

  return `${startStr}${join}${endStr}`;
};

export const fmtTime = (value?: string | number | null, options?: Intl.DateTimeFormatOptions) => {
  if (!value) return null;

  const time = new Date(value);

  const formattedTime = time
    .toLocaleString(LOCALE, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: TIMEZONE,
      ...options,
    })
    .replace(' ', '');

  return formattedTime;
};

export const fmtTimeRange = (
  start?: string | number | null,
  end?: string | number | null,
  join = '-',
  options?: Intl.DateTimeFormatOptions
) => {
  if (!start) return fmtTime(end, options);
  if (!end) return fmtTime(start, options);

  return [fmtTime(start, { ...options, timeZoneName: undefined }), fmtTime(end, options)].join(
    join
  );
};

export const fmtPercent = (n?: string | number | null) => {
  return `${toNumber(n)}%`;
};

export const fmtMoney = (n?: string | number | null) => {
  return `$${toNumber(n)}`;
};

export const floatPoint = (n: string | number | null) => {
  return toNumber(parseFloat(`${toNumber(n)}`).toPrecision(12));
};

// export const fmtCategoryCount = (category?: Category) => {
//   if (!category) return '';

//   const { __typeId, relatedEntryCount } = category;

//   const { labels } = getEntryTypeSettings(__typeId) ?? {};
//   const { name = 'Items', singularName = 'Item' } = labels ?? {};

//   return `${relatedEntryCount} ${numberedLabel(relatedEntryCount ?? 0, name, singularName)}`;
// };

export const numberedLabel = (n: number | undefined, name: string, nameSingular: string) =>
  (Math.abs(n ?? 0) === 1 ? nameSingular : name).replace('%n', `${n ?? 0}`);

export const removeOrphan = (str: string, charLim = 10) => {
  const lastIndex = str.lastIndexOf(' ');

  if (lastIndex < 1) return str;

  const before = str.slice(0, lastIndex);
  const after = str.slice(lastIndex + 1);

  if (after.length > charLim) return str;

  return (
    <>
      {before}&nbsp;{after}
    </>
  );
};
